'use client';

import {
  Zap,
  CheckCircle2,
  Circle,
  BookOpen,
  Clock,
  ListChecks,
  Bookmark,
  ChevronRight,
} from 'lucide-react';
import type { ReactNode } from 'react';

// TL;DR summary box at top of article
interface TLDRProps {
  children: ReactNode;
  readTime?: string;
}

export function TLDR({ children, readTime }: TLDRProps) {
  return (
    <div className="my-8 not-prose">
      <div className="bg-amber-50 border border-amber-100 rounded-xl p-5 md:p-6">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-8 h-8 rounded-lg bg-amber-400 flex items-center justify-center">
            <Zap className="w-4 h-4 text-white" />
          </div>
          <div className="flex-1">
            <p className="font-semibold text-gray-900">TL;DR</p>
          </div>
          {readTime && (
            <div className="flex items-center gap-1.5 text-sm text-amber-700">
              <Clock className="w-4 h-4" />
              <span>{readTime}</span>
            </div>
          )}
        </div>
        <div className="text-gray-700 leading-relaxed [&>p]:m-0 [&>ul]:m-0 [&>ul]:pl-4 [&>ul]:space-y-1">
          {children}
        </div>
      </div>
    </div>
  );
}

// Key takeaways box
interface KeyTakeawaysProps {
  title?: string;
  takeaways: string[];
}

export function KeyTakeaways({
  title = 'Key Takeaways',
  takeaways,
}: KeyTakeawaysProps) {
  return (
    <div className="my-10 not-prose">
      <div className="bg-gray-50 border border-gray-100 rounded-xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <Bookmark className="w-5 h-5 text-amber-500" />
          <h3 className="font-semibold text-gray-900">{title}</h3>
        </div>
        <ul className="space-y-3">
          {takeaways.map((takeaway, index) => (
            <li key={index} className="flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
              <span className="text-gray-700 leading-relaxed">{takeaway}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

// Action items checklist
interface ActionItem {
  task: string;
  completed?: boolean;
}

interface ActionItemsProps {
  title?: string;
  items: ActionItem[] | string[];
}

export function ActionItems({
  title = 'Action Items',
  items,
}: ActionItemsProps) {
  const normalizedItems: ActionItem[] = items.map((item) =>
    typeof item === 'string' ? { task: item, completed: false } : item
  );

  return (
    <div className="my-10 not-prose">
      <div className="border-2 border-dashed border-gray-200 rounded-xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <ListChecks className="w-5 h-5 text-gray-500" />
          <h3 className="font-semibold text-gray-900">{title}</h3>
        </div>
        <ul className="space-y-3">
          {normalizedItems.map((item, index) => (
            <li key={index} className="flex items-start gap-3">
              {item.completed ? (
                <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
              ) : (
                <Circle className="w-5 h-5 text-gray-300 shrink-0 mt-0.5" />
              )}
              <span
                className={`leading-relaxed ${item.completed ? 'text-gray-400 line-through' : 'text-gray-700'}`}
              >
                {item.task}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

// Chapter/Section header with number
interface ChapterHeaderProps {
  number: number | string;
  title: string;
  subtitle?: string;
}

export function ChapterHeader({ number, title, subtitle }: ChapterHeaderProps) {
  return (
    <div className="my-12 not-prose">
      <div className="flex items-start gap-6">
        <div
          className="text-6xl md:text-7xl font-normal text-gray-200 leading-none select-none"
          style={{ fontFamily: 'var(--font-instrument), Georgia, serif' }}
        >
          {String(number).padStart(2, '0')}
        </div>
        <div className="pt-2">
          <h2
            className="text-2xl md:text-3xl font-normal text-gray-900"
            style={{ fontFamily: 'var(--font-instrument), Georgia, serif' }}
          >
            {title}
          </h2>
          {subtitle && (
            <p className="text-gray-500 mt-2 leading-relaxed">{subtitle}</p>
          )}
        </div>
      </div>
    </div>
  );
}

// Reading progress indicator
interface SectionBreakProps {
  label?: string;
}

export function SectionBreak({ label }: SectionBreakProps) {
  return (
    <div className="my-12 not-prose">
      <div className="flex items-center gap-4">
        <div className="flex-1 h-px bg-gray-200" />
        {label && (
          <>
            <span className="text-xs uppercase tracking-wider text-gray-400 font-medium">
              {label}
            </span>
            <div className="flex-1 h-px bg-gray-200" />
          </>
        )}
      </div>
    </div>
  );
}

// Article series navigation
interface SeriesNavProps {
  series: string;
  current: number;
  total: number;
  prevTitle?: string;
  prevHref?: string;
  nextTitle?: string;
  nextHref?: string;
}

export function SeriesNav({
  series,
  current,
  total,
  prevTitle,
  prevHref,
  nextTitle,
  nextHref,
}: SeriesNavProps) {
  return (
    <div className="my-10 not-prose">
      <div className="bg-gray-50 rounded-xl p-5 border border-gray-100">
        {/* Series info */}
        <div className="flex items-center gap-3 mb-4 pb-4 border-b border-gray-200">
          <BookOpen className="w-4 h-4 text-amber-500" />
          <span className="text-sm text-gray-600">
            Part {current} of {total} in{' '}
            <span className="font-medium text-gray-900">{series}</span>
          </span>
        </div>

        {/* Navigation */}
        <div className="grid sm:grid-cols-2 gap-4">
          {prevHref && prevTitle ? (
            <a
              href={prevHref}
              className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900 transition-colors"
            >
              <ChevronRight className="w-4 h-4 rotate-180" />
              <span className="truncate">{prevTitle}</span>
            </a>
          ) : (
            <div />
          )}
          {nextHref && nextTitle && (
            <a
              href={nextHref}
              className="flex items-center gap-2 text-sm text-gray-900 font-medium hover:text-amber-600 transition-colors sm:justify-end"
            >
              <span className="truncate">{nextTitle}</span>
              <ChevronRight className="w-4 h-4" />
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

// Prerequisites or requirements box
interface PrerequisitesProps {
  title?: string;
  items: string[];
}

export function Prerequisites({
  title = "Before you start",
  items,
}: PrerequisitesProps) {
  return (
    <div className="my-8 not-prose">
      <div className="bg-blue-50 border border-blue-100 rounded-xl p-5">
        <p className="text-xs uppercase tracking-wider text-blue-600 font-medium mb-3">
          {title}
        </p>
        <ul className="space-y-2">
          {items.map((item, index) => (
            <li key={index} className="flex items-start gap-2 text-sm text-blue-900">
              <ChevronRight className="w-4 h-4 text-blue-400 shrink-0 mt-0.5" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
